import { FC, useLayoutEffect, useRef, useState } from 'react';

import { WHEEL_PADDING_ROWS, WHEEL_ROW_HEIGHT } from './constants';
import { StyledWheelColumn, StyledWheelRow, StyledWheelSpacer } from './WheelColumn.styled';

const SETTLE_DELAY_MS = 120;

export interface WheelColumnProps {
  items: string[];
  selectedIndex: number;
  onSelect: (index: number) => number;
  'aria-label': string;
}

const WheelColumn: FC<WheelColumnProps> = ({
  items,
  selectedIndex,
  onSelect,
  'aria-label': ariaLabel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [centeredIndex, setCenteredIndex] = useState(selectedIndex);

  const scrollToIndex = (index: number, smooth: boolean) => {
    containerRef.current?.scrollTo({
      top: index * WHEEL_ROW_HEIGHT,
      behavior: smooth ? 'smooth' : 'instant',
    });
  };

  // Keep scroll position in sync when selectedIndex changes externally
  // (e.g. day count changes after a month/year change elsewhere).
  useLayoutEffect(() => {
    scrollToIndex(selectedIndex, false);
    setCenteredIndex(selectedIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, items.length]);

  const handleRowClick = (index: number) => {
    const resolvedIndex = onSelect(index);
    setCenteredIndex(resolvedIndex);
    scrollToIndex(resolvedIndex, true);
  };

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const rawIndex = Math.round(container.scrollTop / WHEEL_ROW_HEIGHT);
    const clampedIndex = Math.min(Math.max(rawIndex, 0), items.length - 1);
    setCenteredIndex(clampedIndex);

    if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
    settleTimeoutRef.current = setTimeout(() => {
      if (clampedIndex === selectedIndex) return;
      const resolvedIndex = onSelect(clampedIndex);
      if (resolvedIndex !== clampedIndex) {
        setCenteredIndex(resolvedIndex);
        scrollToIndex(resolvedIndex, true);
      }
    }, SETTLE_DELAY_MS);
  };

  return (
    <StyledWheelColumn
      ref={containerRef}
      onScroll={handleScroll}
      role="listbox"
      aria-label={ariaLabel}
    >
      {Array.from({ length: WHEEL_PADDING_ROWS }, (_, i) => (
        <StyledWheelSpacer key={`spacer-top-${i}`} aria-hidden />
      ))}
      {items.map((label, index) => (
        <StyledWheelRow
          key={`${label}-${index}`}
          $centered={index === centeredIndex}
          onClick={() => handleRowClick(index)}
          role="option"
          aria-selected={index === selectedIndex}
        >
          {label}
        </StyledWheelRow>
      ))}
      {Array.from({ length: WHEEL_PADDING_ROWS }, (_, i) => (
        <StyledWheelSpacer key={`spacer-bottom-${i}`} aria-hidden />
      ))}
    </StyledWheelColumn>
  );
};

WheelColumn.displayName = 'WheelColumn';

export default WheelColumn;
